import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, MapPin, Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ImportCemeteriesDialog } from "@/components/cemeteries/ImportCemeteriesDialog";

interface Cemetery {
  id: string;
  name: string;
  address: string;
  city: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
  parish_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  requires_approval: boolean;
  created_at: string;
  updated_at: string;
}

interface CemeteryFormData {
  name: string;
  address: string;
  city: string;
  postal_code: string;
  latitude: string;
  longitude: string;
  parish_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  requires_approval: boolean;
}

const Cemeteries = () => {
  
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [selectedCemetery, setSelectedCemetery] = useState<Cemetery | null>(null);
  const [formData, setFormData] = useState<CemeteryFormData>({
    name: "",
    address: "",
    city: "",
    postal_code: "",
    latitude: "",
    longitude: "",
    parish_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    requires_approval: false,
  });

  const { data: cemeteries, isLoading } = useQuery({
    queryKey: ["cemeteries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cemeteries")
        .select("*")
        .is("deleted_at", null)
        .order("name");
      
      if (error) throw error;
      return data as Cemetery[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CemeteryFormData) => {
      const { error } = await supabase.from("cemeteries").insert({
        name: data.name,
        address: data.address,
        city: data.city,
        postal_code: data.postal_code,
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
        parish_name: data.parish_name || null,
        contact_name: data.contact_name || null,
        contact_email: data.contact_email || null,
        contact_phone: data.contact_phone || null,
        requires_approval: data.requires_approval,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cemeteries"] });
      setIsAddEditDialogOpen(false);
      resetForm();
      toast.success("Hautausmaa lisätty onnistuneesti");
    },
    onError: () => {
      toast.error("Virhe hautausmaan lisäämisessä");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CemeteryFormData & { id: string }) => {
      const { error } = await supabase
        .from("cemeteries")
        .update({
          name: data.name,
          address: data.address,
          city: data.city,
          postal_code: data.postal_code,
          latitude: data.latitude ? parseFloat(data.latitude) : null,
          longitude: data.longitude ? parseFloat(data.longitude) : null,
          parish_name: data.parish_name || null,
          contact_name: data.contact_name || null,
          contact_email: data.contact_email || null,
          contact_phone: data.contact_phone || null,
          requires_approval: data.requires_approval,
        })
        .eq("id", data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cemeteries"] });
      setIsAddEditDialogOpen(false);
      resetForm();
      toast.success("Hautausmaa päivitetty onnistuneesti");
    },
    onError: () => {
      toast.error("Virhe hautausmaan päivittämisessä");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("cemeteries")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cemeteries"] });
      setIsDeleteDialogOpen(false);
      setSelectedCemetery(null);
      toast.success("Hautausmaa poistettu onnistuneesti");
    },
    onError: () => {
      toast.error("Virhe hautausmaan poistamisessa");
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      city: "",
      postal_code: "",
      latitude: "",
      longitude: "",
      parish_name: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      requires_approval: false,
    });
    setSelectedCemetery(null);
  };

  const handleAddNew = () => {
    resetForm();
    setIsAddEditDialogOpen(true);
  };

  const handleEdit = (cemetery: Cemetery) => {
    setSelectedCemetery(cemetery);
    setFormData({
      name: cemetery.name,
      address: cemetery.address,
      city: cemetery.city,
      postal_code: cemetery.postal_code,
      latitude: cemetery.latitude?.toString() || "",
      longitude: cemetery.longitude?.toString() || "",
      parish_name: cemetery.parish_name || "",
      contact_name: cemetery.contact_name || "",
      contact_email: cemetery.contact_email || "",
      contact_phone: cemetery.contact_phone || "",
      requires_approval: cemetery.requires_approval,
    });
    setIsAddEditDialogOpen(true);
  };

  const handleDelete = (cemetery: Cemetery) => {
    setSelectedCemetery(cemetery);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCemetery) {
      updateMutation.mutate({ ...formData, id: selectedCemetery.id });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredCemeteries = cemeteries?.filter((cemetery) =>
    cemetery.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cemetery.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cemetery.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Hautausmaat</h1>
          <div className="flex gap-2">
            <Button onClick={() => setIsImportDialogOpen(true)} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Tuo OSM
            </Button>
            <Button onClick={() => navigate("/cemeteries/map")} variant="outline">
              <MapPin className="mr-2 h-4 w-4" />
              Karttanäkymä
            </Button>
            <Button onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              Lisää hautausmaa
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Hae nimen, kaupungin tai osoitteen perusteella..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nimi</TableHead>
                <TableHead>Osoite</TableHead>
                <TableHead>Postinumero</TableHead>
                <TableHead>Kaupunki</TableHead>
                <TableHead>Koordinaatit</TableHead>
                <TableHead className="text-right">Toiminnot</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Ladataan...
                  </TableCell>
                </TableRow>
              ) : filteredCemeteries?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Ei hautausmaita
                  </TableCell>
                </TableRow>
              ) : (
                filteredCemeteries?.map((cemetery) => (
                  <TableRow key={cemetery.id}>
                    <TableCell className="font-medium">{cemetery.name}</TableCell>
                    <TableCell>{cemetery.address}</TableCell>
                    <TableCell>{cemetery.postal_code}</TableCell>
                    <TableCell>{cemetery.city}</TableCell>
                    <TableCell>
                      {cemetery.latitude && cemetery.longitude
                        ? `${cemetery.latitude.toFixed(6)}, ${cemetery.longitude.toFixed(6)}`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(cemetery)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(cemetery)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={isAddEditDialogOpen} onOpenChange={setIsAddEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {selectedCemetery ? "Muokkaa hautausmaata" : "Lisää hautausmaa"}
              </DialogTitle>
              <DialogDescription>
                Täytä hautausmaan tiedot. Koordinaatit haetaan automaattisesti osoitteen perusteella.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nimi *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="address">Osoite *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Postinumero *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Kaupunki *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Leveyspiiri (valinnainen)</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      placeholder="Haetaan automaattisesti"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Pituuspiiri (valinnainen)</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      placeholder="Haetaan automaattisesti"
                    />
                  </div>
                </div>

                {/* Parish contact section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Seurakunnan tiedot</h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="parish_name">Seurakunnan nimi</Label>
                      <Input
                        id="parish_name"
                        value={formData.parish_name}
                        onChange={(e) => setFormData({ ...formData, parish_name: e.target.value })}
                        placeholder="esim. Helsingin seurakuntayhtymä"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_name">Yhteyshenkilö</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_email">Sähköposti</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        placeholder="Hyväksyntäpyynnöt lähetetään tähän"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone">Puhelin</Label>
                      <Input
                        id="contact_phone"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="requires_approval"
                        checked={formData.requires_approval}
                        onCheckedChange={(checked) => 
                          setFormData({ ...formData, requires_approval: checked === true })
                        }
                      />
                      <Label htmlFor="requires_approval" className="font-normal">
                        Vaatii seurakunnan hyväksynnän ennen asennusta
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddEditDialogOpen(false)}
                >
                  Peruuta
                </Button>
                <Button type="submit">
                  {selectedCemetery ? "Tallenna" : "Lisää"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Vahvista poisto</AlertDialogTitle>
              <AlertDialogDescription>
                Haluatko varmasti poistaa hautausmaan "{selectedCemetery?.name}"?
                Tämä toiminto voidaan peruuttaa.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Peruuta</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => selectedCemetery && deleteMutation.mutate(selectedCemetery.id)}
              >
                Poista
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import from OSM Dialog */}
        <ImportCemeteriesDialog
          open={isImportDialogOpen}
          onOpenChange={setIsImportDialogOpen}
        />
      </div>
    </Layout>
  );
};

export default Cemeteries;
